import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { isEmpty } from 'lodash';
import { concat, defer, Subscription, timer } from 'rxjs';
import { Intersection, MathUtils, Mesh, Object3D, Vector3 } from 'three';
import { Project } from '../core/objects/project';
import { User } from '../core/objects/user';
import { ColorsService } from '../core/services/colors.service';
import { ProjectService } from '../core/services/project.service';
import { UserService } from '../core/services/user.service';
import { WhiteLabelService } from '../core/services/white-label.service';
import {
  CargoSpace,
  CompanyBranding,
  Group,
  LoadingSpaceType,
  OutsideSpace,
  Session,
  WorkSpace,
} from '../core/types';
import { ProjectApiService } from './../core/services/project-api.service';
import { SharedProjectService } from './../core/services/shared-project.service';
import { Load } from './visualisation/load';
import { LoadFactory } from './visualisation/loadFactory';
import { MainView } from './visualisation/main-view';
import { CookieService } from 'ngx-cookie-service';
import { CollisionDetection } from './components/load-context-menu/collisionDetection';
import { rotationData } from './components/load-context-menu/load-context-menu.component';
import { RotationTool } from './components/load-context-menu/rotationTool';
import { calculateRotatedObjectData } from './visualisation/utils/boundingBoxData';
import { Message, MessageService } from '../core/services/message.service';
import { prepareMultiStopGroups } from './visualisation/shared-view-functions';
import { convertProjectDimensions } from '../shared/helpers/unitRecalculationHelpers';
import {
  cargoSpaceHasLoadedPalletLayers,
  expandLinkedPalletContentsForVehicle,
  getAllProjectCargoSpaces,
  isPalletContentsViewEnabled,
} from '../shared/helpers/pallet-loading-space.helper';

export interface SharedMultistopGroups {
  groups: Group[];
  unloading: OutsideSpace;
  stopIndex: number;
}

// import { calculateRotatedObjectData } from './visualisation/utils/boundingBoxData';

@Component({
    selector: 'gl-working-table',
    templateUrl: './working-table.component.html',
    styleUrls: ['./working-table.component.scss'],
    standalone: false
})
export class WorkingTableComponent implements OnDestroy, AfterViewChecked {
  @ViewChild('visualisationContainer', { static: false, read: ElementRef })
  public visualisationContainer: ElementRef<HTMLElement>;
  @Input() public isMobile: boolean;
  public rightClickedLoad: Load;
  public rightClickedLoadOriginal: any;
  public mousePosition: any;
  public visualisation: MainView;
  public currentWorkSpace = 0;
  public currentStopIndex: number;
  public user: User;
  public startedOrdedView: boolean;
  public isSharedViewing: boolean;
  public currentObserver: number;
  public observers = [];
  public currentGroup: Group;
  public whiteLabel: CompanyBranding;
  public measurePoint: number;
  public project: Project;
  private height: number;
  private width: number;
  private subs: Subscription[] = [];
  private deferOfGroupsOrder: Subscription;
  public isHint = false;
  public hoveredLoadName = '';
  public rotationY = 0;
  public rotationX = 0;
  private originalPosition: Vector3;
  private multiStopGroups: SharedMultistopGroups[] = [];

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private userService: UserService,
    private elRef: ElementRef<HTMLElement>,
    private sharedProjectService: SharedProjectService,
    private ngZone: NgZone,
    private colorsService: ColorsService,
    private router: Router,
    private whiteLabelService: WhiteLabelService,
    private cookieService: CookieService,
    private messageService: MessageService,
  ) {
    this.whiteLabel = this.whiteLabelService.getState();
    this.subs.push(
      this.projectService.project.pipe().subscribe((project: Project) => {
        this.project = project;
      }),
    );
    this.subs.push(
      this.messageService.getMessages().subscribe((message: Message): void => {
        if (!message) return;
        switch (message.name) {
          case 'startOrderView':
            this.startOrderView();
            break;
          case 'stopOrderView':
            this.stopOrderView();
            break;
          case 'nextOrderGroup':
            this.nextOrderGroup();
            break;
          case 'previousOrderGroup':
            this.previousOrderGroup();
            break;
          case 'updateVisualization':
            // Force visualization update after groups order change in drag and drop
            if (this.visualisation && this.project) {
              this.updateVisualisation(this.project);
            }
            break;
        }
      }),
    );
    this.subs.push(this.userService.user.subscribe((user: User) => (this.user = user)));
    this.subs.push(
      this.projectService.currentWorkSpaceIndex.pipe().subscribe((currentWorkSpace) => {
        if (this.deferOfGroupsOrder) {
          this.deferOfGroupsOrder.unsubscribe();
        }
        if (this.currentObserver !== undefined) {
          this.currentObserver = undefined;
          this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
        }
        this.currentWorkSpace = currentWorkSpace;
        this.startedOrdedView = false;
        if (this.visualisation) {
          this.resetView();
          this.cleanMemory();
        }
      }),
    );
    this.subs.push(
      this.projectService.currentStopIndex.subscribe((currentIndex) => {
        if (this.deferOfGroupsOrder) {
          this.deferOfGroupsOrder.unsubscribe();
        }
        if (this.currentObserver !== undefined) {
          this.currentObserver = undefined;
          this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
        }
        this.currentStopIndex = currentIndex;
        this.startedOrdedView = false;
        if (this.visualisation) {
          LoadFactory.clear();
          this.resetView();
        }
      }),
    );
    this.renderVizualization();
  }

  public ngOnDestroy() {
    LoadFactory.clear();
    if (this.visualisation && this.visualisation.renderingContext) {
      this.visualisation.renderingContext.scene.remove(
        ...this.visualisation.renderingContext.scene.children,
      );
      this.visualisation.renderingContext.renderer.dispose();
      this.visualisation.renderingContext.renderer.forceContextLoss();
    }
    this.subs.forEach((sub: Subscription) => sub.unsubscribe());
  }

  private cleanMemory(): void {
    const scene = this.visualisation.renderingContext.scene;
    scene.traverse((object: Mesh) => {
      if (object instanceof Load && Object.prototype.hasOwnProperty.call(object, 'geometry')) {
        object.geometry.dispose();
      }
    });
    LoadFactory.clear();
  }

  public renderVizualization() {
    window.setTimeout(() => {
      this.subs.push(
        this.projectService.project.pipe().subscribe((project: Project) => {
          const isMm = project.options.unit === 'mm';
          if (!this.visualisation && project.workSpaces.length !== 0) {
            if (isMm) {
              project = convertProjectDimensions(project, false);
            }
            this.initializeVisualisation(project);
            this.manageVisualisationInteractionActivity();
            this.manageMobileVisualisationInteractionActivity();
            this.manageVisualisationMagnetActicity(project.options.isMagnetOn);
          } else if (this.visualisation) {
            if (isMm) {
              project = convertProjectDimensions(project, false);
            }
            this.updateVisualisation(project);
            this.manageMobileVisualisationInteractionActivity();
            this.manageVisualisationMagnetActicity(project.options.isMagnetOn);
          }
        }),
      );
    }, 10);
  }

  public ngAfterViewChecked() {
    if (
      (this.visualisationContainer.nativeElement.offsetWidth !== this.width ||
        this.visualisationContainer.nativeElement.offsetHeight !== this.height) &&
      this.visualisation
    ) {
      if (this.width !== undefined && this.height !== undefined) {
        this.visualisation.onWindowResize();
      }
      this.width = this.visualisationContainer.nativeElement.clientWidth;
      this.height = this.visualisationContainer.nativeElement.clientHeight;
    }
  }

  public getWidth() {
    return this.elRef.nativeElement.clientWidth;
  }

  public getHeight() {
    return this.elRef.nativeElement.clientHeight;
  }

  private changeInteractivity(isInteractable: boolean): void {
    if (!isInteractable) {
      this.visualisation.disableMouseActions();
      this.visualisation.disableDrag();
      this.visualisation.renderingContext.cameraControls.enabled = false;
    } else {
      this.visualisation.enableMouseActions();
      this.visualisation.enableDrag();
      this.visualisation.renderingContext.cameraControls.enabled = true;
      this.visualisation.renderingContext.renderer.domElement.style.cursor = 'default';
    }
  }

  private startMouseRotateY(): void {
    const domElement = this.visualisation.renderingContext.renderer.domElement;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.mouseRotateObjectY = this.mouseRotateObjectY.bind(this);
    domElement.addEventListener('pointermove', this.mouseRotateObjectY);
  }

  private startMouseRotateX(): void {
    const domElement = this.visualisation.renderingContext.renderer.domElement;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.mouseRotateObjectX = this.mouseRotateObjectX.bind(this);
    domElement.addEventListener('pointermove', this.mouseRotateObjectX);
  }

  private mouseRotateObjectY(event): void {
    let howMuchToRotate = Math.atan(event.movementX / 100);
    const isNegative = howMuchToRotate < 0;
    howMuchToRotate =
      Math.abs(howMuchToRotate) <= 0.1 ? howMuchToRotate : isNegative ? -0.01 : 0.01;
    this.rotateY(howMuchToRotate);
  }

  private mouseRotateObjectX(event): void {
    let howMuchToRotate = Math.atan(event.movementY / 100);
    const isNegative = howMuchToRotate < 0;
    howMuchToRotate =
      Math.abs(howMuchToRotate) <= 0.1 ? howMuchToRotate : isNegative ? -0.01 : 0.01;
    this.rotateX(howMuchToRotate);
  }

  private rotateY(rad: number): void {
    this.clearBoundingBoxes();
    const object = this.visualisation.renderingContext.scene.getObjectById(
      this.rightClickedLoadOriginal.id,
    ) as Load;
    if (!object) {
      return;
    }
    if (object.rotation.x !== 0) {
      this.rotateYX(rad);
      return;
    }
    const objectClone = object;
    const isPivotParent = object.parent?.name?.includes('pivot');
    let pivot: Object3D;
    let container: Object3D;
    if (isPivotParent) {
      pivot = object.parent;
      container = object.parent.parent;
    } else {
      container = new Object3D();
      container.name = `container_${object.name}`;
      const pivot = new Object3D();
      pivot.name = `pivot_${object.name}`;
      const objectClone = object;
      pivot.add(objectClone);
      pivot.rotation.set(0, object.yRotation / 100, 0);
      container.add(pivot);
      pivot.position.set(
        object.position.x + object.width / 2,
        object.position.y,
        object.position.z + object.length / 2,
      );
      objectClone.position.set(-objectClone.width / 2, 0, -objectClone.length / 2);
      this.visualisation.renderingContext.scene.add(container);
    }
    const collisionDetection: CollisionDetection = new CollisionDetection(this.visualisation);

    if (isPivotParent) {
      this.rotationY = pivot.rotation.y;
      pivot.rotateY(rad);
      const isInCollision: boolean = collisionDetection.isInCollision(
        objectClone.parent.parent,
        false,
        objectClone.id,
      );
      if (isInCollision) {
        pivot.rotateY(-rad);
      }
      this.attachRotationTool(object, pivot);
    }
  }

  private attachRotationTool(object: Load, pivot: Object3D, isXRotation = false): void {
    const rotationToolCreator = new RotationTool();
    const isRotationTool =
      this.visualisation.renderingContext.scene.getObjectByName('rotationToolGroup');
    const isrotationToolGroupClone =
      this.visualisation.renderingContext.scene.getObjectByName('rotationToolGroupClone');
    const objectData = calculateRotatedObjectData(object);
    const rotationTool = isrotationToolGroupClone
      ? isRotationTool
      : rotationToolCreator.drawRotationTool(object);
    if (!isRotationTool) {
      const clonedObject = new Object3D();
      clonedObject.name = 'rotationToolGroupClone';
      clonedObject.position.copy(pivot.position);
      clonedObject.rotation.copy(pivot.rotation);
      if (!isXRotation) {
        rotationTool.rotation.set(MathUtils.degToRad(90), 0, 0);
        rotationTool.position.set(0, objectData.height + 10, 0);
      } else {
        rotationTool.rotation.set(object.rotation.x, Math.PI / 2, Math.PI);
        rotationTool.position.set(
          object.position.x + object.width / 2,
          object.position.y,
          object.position.z,
        );
      }
      clonedObject.add(rotationTool);
      this.visualisation.renderingContext.scene.add(clonedObject);
    } else {
      isrotationToolGroupClone.position.copy(pivot.position);
      isrotationToolGroupClone.rotation.copy(pivot.rotation);
      if (!isXRotation) {
        isRotationTool.rotation.set(MathUtils.degToRad(90), 0, 0);
        isRotationTool.position.set(0, objectData.height + 10, 0);
      } else {
        rotationTool.rotation.set(object.rotation.x, Math.PI / 2, Math.PI);
        rotationTool.position.set(
          object.position.x + object.width / 2,
          object.position.y,
          object.position.z,
        );
      }
    }
  }

  private rotateYX(rad: number): void {
    const object = this.visualisation.renderingContext.scene.getObjectById(
      this.rightClickedLoadOriginal.id,
    ) as Load;
    const objectClone = object;
    const isPivotParent = object.parent?.name?.includes('pivot');
    this.clearBoundingBoxes();
    const pivot = object.parent;
    const collisionDetection: CollisionDetection = new CollisionDetection(this.visualisation);
    if (!isPivotParent) {
      pivot.add(objectClone);
      pivot.position.set(
        objectClone.position.x + objectClone.width / 2,
        objectClone.position.y,
        objectClone.position.z + objectClone.length / 2,
      );
      objectClone.position.set(-objectClone.width / 2, 0, -objectClone.length / 2);
      this.visualisation.renderingContext.scene.add(pivot);
    } else {
      pivot.rotateY(rad);
      const isInCollision: boolean = collisionDetection.isInCollision(
        objectClone.parent.parent,
        false,
        objectClone.id,
      );
      if (isInCollision) {
        pivot.rotateY(-rad);
      }
    }
    this.attachRotationTool(object, pivot);
  }

  private rotateXY(rad: number): void {
    this.clearBoundingBoxes();
    const object = this.visualisation.renderingContext.scene.getObjectById(
      this.rightClickedLoadOriginal.id,
    ) as Load;
    this.rotationX = object.rotation.x;
    let rotation;
    rotation = this.rotationX + rad < -(Math.PI / 2) ? -(Math.PI / 2) : this.rotationX + rad;
    rotation = rotation > 0 ? 0 : (rotation as number);
    const collisionDetection: CollisionDetection = new CollisionDetection(this.visualisation);
    object.rotation.set(rotation, object.rotation.y, object.rotation.z);
    const isInCollision: boolean = collisionDetection.isInCollision(
      object.parent.parent,
      false,
      object.id,
      true,
    );
    if (isInCollision) {
      object.rotateX(-rad);
    }
    this.attachRotationTool(object, object.parent, true);
  }

  private rotateX(rad: number): void {
    this.clearBoundingBoxes();
    const object: Object3D = this.visualisation.renderingContext.scene.getObjectById(
      this.rightClickedLoadOriginal.id,
    );
    const isPivotParent = object.parent?.name?.includes('pivot');
    if (isPivotParent) {
      this.rotateXY(rad);
      return;
    }
    const collisionDetection: CollisionDetection = new CollisionDetection(this.visualisation);
    let rotation;
    rotation = this.rotationX + rad < -(Math.PI / 2) ? -(Math.PI / 2) : this.rotationX + rad;
    rotation = rotation > 0 ? 0 : (rotation as number);
    object.translateX(this.rightClickedLoadOriginal.width / 2);
    object.rotation.set(rotation, 0, 0);
    object.translateX(-this.rightClickedLoadOriginal.width / 2);
    const isInCollision: boolean = collisionDetection.isInCollision(object, false, object.id, true);
    if (isInCollision) {
      object.rotation.set(this.rotationX, 0, 0);
    } else {
      this.rotationX = rotation as number;
    }
    this.attachRotationTool(object as Load, object.parent, true);
  }

  private clearRotationTool(): void {
    const rotationTool =
      this.visualisation.renderingContext.scene.getObjectByName('rotationToolGroup');
    const rotationToolGroupClone =
      this.visualisation.renderingContext.scene.getObjectByName('rotationToolGroupClone');
    if (rotationTool) {
      this.visualisation.renderingContext.scene.remove(rotationTool);
    }
    if (rotationToolGroupClone) {
      this.visualisation.renderingContext.scene.remove(rotationToolGroupClone);
    }
  }

  private clearBoundingBoxes(): void {
    const box3Helpers = this.visualisation.renderingContext.scene.children.filter(
      (child) => child.type === 'Box3Helper',
    );
    box3Helpers.forEach((box) => this.visualisation.renderingContext.scene.remove(box));
  }

  private sendRotation(): void {
    this.clearBoundingBoxes();
    this.clearRotationTool();
    const object = this.visualisation.renderingContext.scene.getObjectById(
      this.rightClickedLoadOriginal.id,
    );
    const isPivotParent = object.parent?.name?.includes('pivot');
    let pivotRotationY: number = undefined;
    if (isPivotParent) {
      object.visible = true;
      const pivotQuaternion = object.parent.quaternion.clone();
      pivotRotationY = Math.atan2(
        2 * (pivotQuaternion.w * pivotQuaternion.y + pivotQuaternion.x * pivotQuaternion.z),
        1 - 2 * (pivotQuaternion.y * pivotQuaternion.y + pivotQuaternion.x * pivotQuaternion.x),
      );
    }
    this.rotationY = this.rightClickedLoadOriginal.rotation.y as number;
    this.rotationX = this.rightClickedLoadOriginal.rotation.x as number;
    this.originalPosition = { ...this.rightClickedLoadOriginal.oldPosition } as Vector3;

    if (!object) {
      return;
    }
    const calcPosition = new Vector3(
      this.originalPosition.x / 100,
      this.originalPosition.y / 100,
      this.originalPosition.z / 100,
    );
    const dataToSend: rotationData = {
      yRotation: isPivotParent ? pivotRotationY * 100 : object.rotation.y * 100,
      edgeRotation: object.rotation.x * 100,
      objectName: this.rightClickedLoadOriginal.name as string,
      currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
      target: {
        workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        position: calcPosition,
        stopId:
          this.currentStopIndex >= 0
            ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
            : undefined,
      },
      currentStopId:
        this.currentStopIndex >= 0
          ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
          : undefined,
    };
    this.visualisation.renderingContext.scene.remove(object.parent);
    this.visualisation.renderingContext.scene.remove(object.parent.parent);
     
    this.projectApiService.rotateLoad(dataToSend).subscribe();
  }

  private endMouseRotate(): void {
    const domElement = this.visualisation.renderingContext.renderer.domElement;
    domElement.removeEventListener('pointerdown', this.startMouseRotateX, false);
    domElement.removeEventListener('pointerdown', this.startMouseRotateY, false);
    domElement.removeEventListener('pointermove', this.mouseRotateObjectX, false);
    domElement.removeEventListener('pointermove', this.mouseRotateObjectY, false);
    domElement.removeEventListener('pointerup', this.endMouseRotate, false);
    this.changeInteractivity(true);
    this.sendRotation();
  }

  public closeContextMenuEmit(event: any) {
    this.rotationY = this.rightClickedLoadOriginal.rotation.y as number;
    this.rotationX = this.rightClickedLoadOriginal.rotation.x as number;
    this.originalPosition = { ...this.rightClickedLoad.oldPosition } as Vector3;
    if (event === 'mouseRotateY') {
      this.changeInteractivity(false);
      const domElement = this.visualisation.renderingContext.renderer.domElement;
      domElement.style.cursor = 'ew-resize';
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.startMouseRotateY = this.startMouseRotateY.bind(this);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.endMouseRotate = this.endMouseRotate.bind(this);
      domElement.addEventListener('pointerdown', this.startMouseRotateY);
      domElement.addEventListener('pointerup', this.endMouseRotate);
      this.rightClickedLoad = undefined;
    }
    if (event === 'mouseRotateX') {
      this.changeInteractivity(false);
      const domElement = this.visualisation.renderingContext.renderer.domElement;
      domElement.style.cursor = 'ns-resize';
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.startMouseRotateX = this.startMouseRotateX.bind(this);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.endMouseRotate = this.endMouseRotate.bind(this);
      domElement.addEventListener('pointerdown', this.startMouseRotateX);
      domElement.addEventListener('pointerup', this.endMouseRotate);
      this.rightClickedLoad = undefined;
    } else {
      this.rightClickedLoad = undefined;
    }
  }

  public closeContextMenu() {
    this.rightClickedLoad = undefined;
  }

  public closeLoadHint(): void {
    this.isHint = false;
  }

  public closeMeasureHint() {
    this.measurePoint = undefined;
  }

  public resetView() {
    this.visualisation.resetView();
  }

  public isProjectEmpty() {
    return this.project.workSpaces.length === 0;
  }

  private orderStart(): void {
    this.observers.push(
      defer(() => {
        this.setNewSessionWithGroup([]);
        this.currentObserver = null;
        this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
        this.currentGroup = undefined;
        this.messageService.sendMessage({ name: 'currentGroup', data: undefined });
        return timer(1600);
      }),
    );
  }

  private orderMultiStopGroups(multiStopGroups: SharedMultistopGroups[]): void {
    multiStopGroups.forEach((multiStopGroup: SharedMultistopGroups): void => {
      this.observers.push(
        defer(() => {
          this.setNewSessionWithMultiStopGroup(multiStopGroup.groups, multiStopGroup.unloading);
          this.currentGroup =
            multiStopGroup.unloading.groups.length > 0
              ? multiStopGroup.unloading.groups[multiStopGroup.unloading.groups.length - 1]
              : multiStopGroup.groups[multiStopGroup.groups.length - 1];
          this.messageService.sendMessage({
            name: 'currentGroup',
            data: this.currentGroup,
          });
          this.currentObserver = this.currentObserver + 1;
          this.messageService.sendMessage({
            name: 'currentObserver',
            data: this.currentObserver,
          });
          this.messageService.sendMessage({
            name: 'currentDisplayIndex',
            data: multiStopGroup.stopIndex,
          });
          return timer(1600);
        }),
      );
    });
  }

  private orderEnd(): void {
    this.observers.push(
      defer(() => {
        this.currentObserver = undefined;
        this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
        this.messageService.sendMessage({ name: 'currentDisplayIndex', data: null });
        this.startedOrdedView = false;
        this.messageService.sendMessage({ name: 'startedOrderedViewOff' });
        this.currentGroup = undefined;
        this.messageService.sendMessage({ name: 'currentGroup', data: undefined });
      }),
      defer(() => {
        this.messageService.sendMessage({ name: 'resetMultistops', data: null });
        this.updateVisualisation(this.project);
        return timer(1600);
      }),
    );
  }

  public createOrderView(): void {
    const isMultiStop: boolean = this.project.workSpaces[this.currentWorkSpace].stops.length > 0;
    const newGroup: Group[][] = [];
    if (isMultiStop) {
      this.multiStopGroups = [];
      this.orderStart();
      this.project.workSpaces[this.currentWorkSpace].stops.forEach(
        (stop: WorkSpace, index: number): void => {
          prepareMultiStopGroups(
            stop.cargoSpaces[0].groups.concat(
              stop.cargoSpaces[1] ? stop.cargoSpaces[1].groups : [],
            ),
            stop.unloadingSpace,
            index,
            this.projectService,
            this.project,
            this.multiStopGroups,
            this.currentWorkSpace,
          );
        },
      );
      this.orderMultiStopGroups(this.multiStopGroups);
      this.orderEnd();
      this.deferOfGroupsOrder = defer(() => concat(...this.observers)).subscribe();
    } else {
      this.observers.push(
        defer(() => {
          this.setNewSessionWithGroup([], null);
          this.currentObserver = null;
          this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
          this.currentGroup = undefined;
          this.messageService.sendMessage({ name: 'currentGroup', data: undefined });
          return timer(1600);
        }),
      );
      let groups: Group[] = [];
      this.project.workSpaces[this.currentWorkSpace].cargoSpaces.forEach(
        (cargoSpace: CargoSpace): void => {
          groups = groups.concat(cargoSpace.groups);
        },
      );
      if (groups.length > 0) {
        groups = this.projectService.getProjectGroupOrder(groups);
      }

      groups.forEach((group: Group, index: number): void => {
        if (isEmpty(newGroup)) {
          newGroup.push([group]);
        } else {
          newGroup.push([...newGroup[index - 1], group]);
        }

        this.observers.push(
          defer(() => {
            this.setNewSessionWithGroup(newGroup[index], null);
            this.currentGroup = group;
            this.messageService.sendMessage({ name: 'currentGroup', data: group });
            this.currentObserver = index;
            this.messageService.sendMessage({
              name: 'currentObserver',
              data: this.currentObserver,
            });
            return timer(1600);
          }),
        );
      });

      this.observers.push(
        defer(() => {
          this.currentObserver = undefined;
          this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
          this.startedOrdedView = false;
          this.messageService.sendMessage({ name: 'startedOrderedViewOff' });
          this.currentGroup = undefined;
          this.messageService.sendMessage({ name: 'currentGroup', data: undefined });
          this.updateVisualisation(this.project);
          this.deferOfGroupsOrder.unsubscribe();
        }),
      );
      this.deferOfGroupsOrder = defer(() => concat(...this.observers)).subscribe();
    }
  }

  public stopOrderView(skip?: boolean) {
    this.deferOfGroupsOrder.unsubscribe();
    if (!skip) {
      this.startedOrdedView = false;
    }
  }

  public startOrderView(): void {
    this.startedOrdedView = true;
    if (this.currentObserver !== undefined) {
      this.deferOfGroupsOrder = defer(() =>
        concat(
          ...this.observers.filter((_obj, index: number): boolean => index > this.currentObserver),
        ),
      ).subscribe();
    } else {
      this.createOrderView();
    }
  }

  public nextOrderGroup(): void {
    if (!this.startedOrdedView && (this.currentObserver < 0 || !this.currentObserver)) {
      this.currentObserver = this.currentObserver === null ? -1 : this.currentObserver;
      this.startOrderView();
      this.stopOrderView(false);
    }

    const isMultiStop: boolean = this.project.workSpaces[this.currentWorkSpace].stops.length > 0;
    if (isMultiStop) {
      this.deferOfGroupsOrder.unsubscribe();
      if (this.currentObserver !== undefined) {
        this.currentObserver = this.currentObserver + 1;
        if (this.currentObserver >= this.multiStopGroups.length) {
          this.setNewSessionWithGroup([]);
          this.currentObserver = null;
          this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
          this.messageService.sendMessage({ name: 'currentGroup', data: undefined });
          this.currentGroup = undefined;
        } else {
          const currentMultiStopGroup: SharedMultistopGroups =
            this.multiStopGroups[this.currentObserver];
          this.currentGroup =
            currentMultiStopGroup.unloading.groups.length > 0
              ? currentMultiStopGroup.unloading.groups[
                  currentMultiStopGroup.unloading.groups.length - 1
                ]
              : currentMultiStopGroup.groups[currentMultiStopGroup.groups.length - 1];
          this.messageService.sendMessage({ name: 'currentGroup', data: this.currentGroup });
          this.messageService.sendMessage({
            name: 'currentDisplayIndex',
            data: currentMultiStopGroup.stopIndex,
          });
          this.setNewSessionWithMultiStopGroup(
            currentMultiStopGroup.groups,
            currentMultiStopGroup.unloading,
          );
        }
        this.startedOrdedView = false;
        this.messageService.sendMessage({ name: 'startedOrderedViewOff' });
      }
    } else {
      if (this.startedOrdedView && this.currentObserver !== undefined) {
        this.deferOfGroupsOrder.unsubscribe();
        this.deferOfGroupsOrder = defer(() =>
          concat(
            ...this.observers.filter(
              (_obj, index: number): boolean => index > this.currentObserver + 1,
            ),
          ),
        ).subscribe();
      } else if (!this.startedOrdedView && this.currentObserver !== undefined) {
        if (Number.isInteger(this.currentObserver)) {
          this.deferOfGroupsOrder = defer(() =>
            concat(
              ...this.observers.filter(
                (_obj, index: number): boolean => index > this.currentObserver + 1,
              ),
            ),
          ).subscribe();
        } else {
          this.deferOfGroupsOrder = defer(() =>
            concat(
              ...this.observers.filter(
                (_obj, index: number): boolean => index > this.currentObserver,
              ),
            ),
          ).subscribe();
        }
        this.deferOfGroupsOrder.unsubscribe();
      }
    }
  }

  public previousOrderGroup(): void {
    const isMultiStop: boolean = this.project.workSpaces[this.currentWorkSpace].stops.length > 0;
    if (isMultiStop) {
      this.deferOfGroupsOrder.unsubscribe();
      if (this.currentObserver !== undefined) {
        this.currentObserver = this.currentObserver - 1;
        if (this.currentObserver < 0) {
          this.setNewSessionWithGroup([]);
          this.currentObserver = null;
          this.messageService.sendMessage({ name: 'currentObserver', data: this.currentObserver });
          this.messageService.sendMessage({ name: 'currentGroup', data: undefined });
          this.currentGroup = undefined;
        } else {
          const currentMultiStopGroup: SharedMultistopGroups =
            this.multiStopGroups[this.currentObserver];
          this.currentGroup =
            currentMultiStopGroup.unloading.groups.length > 0
              ? currentMultiStopGroup.unloading.groups[
                  currentMultiStopGroup.unloading.groups.length - 1
                ]
              : currentMultiStopGroup.groups[currentMultiStopGroup.groups.length - 1];
          this.messageService.sendMessage({ name: 'currentGroup', data: this.currentGroup });
          this.messageService.sendMessage({
            name: 'currentDisplayIndex',
            data: currentMultiStopGroup.stopIndex,
          });
          this.setNewSessionWithMultiStopGroup(
            currentMultiStopGroup.groups,
            currentMultiStopGroup.unloading,
          );
        }
        this.startedOrdedView = false;
        this.messageService.sendMessage({ name: 'startedOrderedViewOff' });
      }
    } else {
      if (this.startedOrdedView && this.currentObserver !== undefined) {
        this.deferOfGroupsOrder.unsubscribe();
        this.deferOfGroupsOrder = defer(() =>
          concat(
            ...this.observers.filter((_obj, index: number): boolean => {
              return index > this.currentObserver - 1;
            }),
          ),
        ).subscribe();
      } else if (!this.startedOrdedView && this.currentObserver !== undefined) {
        this.deferOfGroupsOrder = defer(() =>
          concat(
            ...this.observers.filter(
              (_obj, index: number): boolean => index > this.currentObserver - 1,
            ),
          ),
        ).subscribe();
        this.deferOfGroupsOrder.unsubscribe();
      }
    }
  }

  public getIconDataUrl(): string {
    return this.colorsService.colorIcons.get(this.currentGroup.color);
  }

  private setNewSessionWithMultiStopGroup(groups: Group[], unloading: OutsideSpace = null): void {
    const newSession: ProjectToSessionMapper = this.createProjectSessionMapper(
      this.project.workSpaces.length > 0
        ? this.project.workSpaces[this.currentWorkSpace].cargoSpaces
        : [],
      this.project.workSpaces.length > 0
        ? this.project.workSpaces[this.currentWorkSpace].outsideSpace
        : undefined,
      this.project.options.unit,
      this.project.workSpaces[this.currentWorkSpace].stops[this.currentStopIndex].unloadingSpace,
    );

    newSession.groups = groups;
    if (unloading !== null) {
      newSession.unloadingSpace = unloading;
    }
    this.cleanMemory();
    this.visualisation.setSession(newSession);
  }

  private setNewSessionWithGroup(group: Group[], unloading?: OutsideSpace): void {
    const unloadingSpace: OutsideSpace = unloading !== null ? unloading : null;

    const newSession: ProjectToSessionMapper = this.createProjectSessionMapper(
      this.project.workSpaces.length > 0
        ? this.project.workSpaces[this.currentWorkSpace].cargoSpaces
        : [],
      this.project.workSpaces.length > 0
        ? this.project.workSpaces[this.currentWorkSpace].outsideSpace
        : undefined,
      this.project.options.unit,
      unloadingSpace,
    );

    newSession.groups = group;
    if (unloading !== null) {
      newSession.unloadingSpace = unloading;
    }
    this.cleanMemory();
    this.visualisation.setSession(newSession);
  }

  private createProjectSessionMapper(
    cargoSpaces: CargoSpace[],
    outsideSpace: OutsideSpace | undefined,
    unit: string,
    unloadingSpace?: OutsideSpace,
  ): ProjectToSessionMapper {
    return new ProjectToSessionMapper(
      cargoSpaces,
      outsideSpace,
      unit,
      unloadingSpace,
      getAllProjectCargoSpaces(this.project),
      isPalletContentsViewEnabled((name) => this.cookieService.get(name)),
    );
  }

  private manageVisualisationInteractionActivity() {
    this.subs.push(
      this.sharedProjectService.isViewingShared.subscribe((isViewing) => {
        this.isSharedViewing = isViewing;
        if (isViewing) {
          this.visualisation.disableDrag();
        } else {
          this.visualisation.enableDrag();
        }
      }),
    );
  }

  private manageMobileVisualisationInteractionActivity() {
    if (this.isMobile) {
      this.visualisation.disableDrag();
    }
  }

  private manageVisualisationMagnetActicity(isMagnetEnabled: boolean) {
    if (isMagnetEnabled) {
      this.visualisation.enableSnapping();
    } else {
      this.visualisation.disableSnapping();
    }
  }

  private modifyProjectAxles(project: Project): Project {
    const modifiedProject: Project = JSON.parse(JSON.stringify(project)) as unknown as Project;
    if (modifiedProject.workSpaces) {
      modifiedProject.workSpaces.forEach((workspace) => {
        if (workspace.axis && workspace.cargoSpaces && workspace.cargoSpaces.length > 0) {
          workspace.cargoSpaces[0].axis = [...workspace.axis];
        }
      });
    }
    return modifiedProject;
  }

  private initializeVisualisation(project: Project): void {
    const isAxis =
      project.workSpaces[this.currentWorkSpace].axis &&
      project.workSpaces[this.currentWorkSpace].axis.length > 0;
    let modifiedProject = project;
    if (isAxis) {
      modifiedProject = this.modifyProjectAxles(project);
    }
    if (this.currentStopIndex >= 0) {
      this.visualisation = new MainView(
        this.visualisationContainer.nativeElement,
        this.createProjectSessionMapper(
          modifiedProject.workSpaces[this.currentWorkSpace].stops[
            this.currentStopIndex
          ].cargoSpaces,
          modifiedProject.workSpaces[this.currentWorkSpace].stops[
            this.currentStopIndex
          ].outsideSpace,
          modifiedProject.options.unit,
          modifiedProject.workSpaces[this.currentWorkSpace].stops[
            this.currentStopIndex
          ].unloadingSpace,
        ),
        this.projectService,
        false,
        modifiedProject.options,
        this.ngZone,
        this.cookieService,
        this.sharedProjectService,
        this.messageService,
      );
    } else {
      this.visualisation = new MainView(
        this.visualisationContainer.nativeElement,
        this.createProjectSessionMapper(
          modifiedProject.workSpaces[this.currentWorkSpace].cargoSpaces,
          modifiedProject.workSpaces[this.currentWorkSpace].outsideSpace,
          modifiedProject.options.unit,
        ),
        this.projectService,
        false,
        modifiedProject.options,
        this.ngZone,
        this.cookieService,
        this.sharedProjectService,
        this.messageService,
      );
    }
    this.projectService.$workingTable.next(this.visualisation);
    this.listenToVisualisationEvents();
    if (this.isMobile) {
      this.visualisation.disableCameraBox();
    }
  }

  private listenToVisualisationEvents() {
    this.visualisationContainer.nativeElement.children[0].addEventListener(
      'loadHover',
      (event: CustomEvent) => {
        if (event.detail.load !== undefined) {
          this.isHint = true;
          this.hoveredLoadName = event.detail.load.nameTranslated;
        } else {
          this.isHint = false;
        }
        this.mousePosition = event.detail.mousePosition;
      },
    );
    this.visualisationContainer.nativeElement.children[0].addEventListener(
      'loaddragged',
      (event: CustomEvent) => {
        const activeCargoSpace =
          this.project.workSpaces[this.currentWorkSpace]?.cargoSpaces?.[0];
        if (cargoSpaceHasLoadedPalletLayers(activeCargoSpace)) {
          this.visualisation.enableDrag();
          return;
        }
        // Convert newPosition from meters/centimeters to internal units (multiply by 100)
        const newPositionInInternalUnits = new Vector3(
          event.detail.newPosition.x * 100,
          event.detail.newPosition.y * 100,
          event.detail.newPosition.z * 100,
        );

        // Determine cargoSpaceId based on whether load is in loading space or outside space
        let cargoSpaceId: number | null = null;
        if (this.visualisation && this.visualisation.loadingSpace) {
          if (this.visualisation.loadingSpace.hasPosition(newPositionInInternalUnits)) {
            // Load is in loading space - determine which cargo space (0 or 1)
            const isMultiStop: boolean =
              this.project.workSpaces[this.currentWorkSpace]?.stops?.length > 0;
            const cargoSpaceIndex: number = this.visualisation.loadingSpace.isInFirstPart(
              newPositionInInternalUnits,
            )
              ? 0
              : this.visualisation.loadingSpace.isInSecondPart(newPositionInInternalUnits)
                ? 1
                : -1;

            if (cargoSpaceIndex >= 0) {
              if (isMultiStop && this.currentStopIndex >= 0) {
                // Multi-stop scenario: cargo spaces are in stops[stopIndex].cargoSpaces
                const stop = this.project.workSpaces[this.currentWorkSpace]?.stops?.[
                  this.currentStopIndex
                ];
                if (stop?.cargoSpaces?.[cargoSpaceIndex]?.id !== undefined) {
                  cargoSpaceId = stop.cargoSpaces[cargoSpaceIndex].id;
                }
              } else {
                // Regular workspace: cargo spaces are in workSpaces[workSpaceIndex].cargoSpaces
                if (
                  this.project.workSpaces[this.currentWorkSpace]?.cargoSpaces?.[cargoSpaceIndex]
                    ?.id !== undefined
                ) {
                  cargoSpaceId =
                    this.project.workSpaces[this.currentWorkSpace].cargoSpaces[cargoSpaceIndex].id;
                }
              }
            }
          }
          // If load is not in loading space (outside space), cargoSpaceId remains null
        }

        this.projectApiService
          .dragLoad({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            currentStopId:
              this.currentStopIndex >= 0
                ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
                : undefined,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              stopId:
                this.currentStopIndex >= 0
                  ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
                  : undefined,
              cargoSpaceId: cargoSpaceId,
              position: event.detail.position,
            },
            newPosition: event.detail.newPosition,
          })
          .subscribe((projectData: Project) => {
            this.cleanMemory();
            this.projectApiService.handleSuccess(projectData);
            this.visualisation.enableDrag();
          });
      },
    );
    this.visualisationContainer.nativeElement.children[0].addEventListener(
      'actions',
      (event: CustomEvent) => {
        if (event.detail.isBlocked) {
          this.rightClickedLoad = undefined;
          return;
        }
        this.rightClickedLoad = event.detail.load;
        this.rightClickedLoadOriginal = event.detail.loadOriginal as Load;
        this.mousePosition = event.detail.mousePosition;
      },
    );

    this.visualisationContainer.nativeElement.children[0].addEventListener('dragstart', () => {
      const isHintsEnabled = this.cookieService.get('hints').includes('true');
      if (isHintsEnabled) {
        this.isHint = false;
      }
    });

    this.visualisationContainer.nativeElement.children[0].addEventListener(
      'measure',
      (event: CustomEvent) => {
        this.closeMeasureHint();
        const isMm: boolean = this.project.options.unit === 'mm';
        const multiplier: number = isMm ? 10 : 1;
        if (event.detail.intersect) {
          if (event.detail.intersect.object.name.includes('Horizontal')) {
            if (event.detail.intersect.object.name.includes('Set')) {
              this.measurePoint = Math.round(
                ((event.detail.intersect.point.x - event.detail.intersect.object.position.x) /
                  100) *
                  multiplier,
              );
            } else {
              this.measurePoint = Math.round((event.detail.intersect.point.x / 100) * multiplier);
            }
          } else if (
            event.detail.intersect.object.name.includes('planeHeight') ||
            event.detail.intersect.object.name.includes('gridHeight') ||
            event.detail.intersect.object.name.includes('grid')
          ) {
            if (event.detail.intersect.object.name.includes('Set')) {
              this.measurePoint = Math.round(
                ((event.detail.intersect.point.y - event.detail.intersect.object.position.y) /
                  100) *
                  multiplier,
              );
            } else {
              const isPallet: boolean = this.visualisation.loadingSpace.type === 3;
              const pointY: number = isPallet
                ? event.detail.intersect.point.y - this.visualisation.loadingSpace.palletHeight
                : event.detail.intersect.point.y;
              this.measurePoint = Math.round(pointY / 100) * multiplier;
            }
          } else {
            if (event.detail.intersect.object.name.includes('Set')) {
              this.measurePoint = Math.round(
                ((event.detail.intersect.point.z - event.detail.intersect.object.position.z) /
                  100) *
                  multiplier,
              );
            } else {
              this.measurePoint = Math.round(event.detail.intersect.point.z / 100) * multiplier;
            }
          }
        }
        this.mousePosition = event.detail.mousePosition;
      },
    );

    this.visualisationContainer.nativeElement.children[0].addEventListener(
      'colorchange',
      (event: CustomEvent) => {
        if (this.router.url.includes('edit')) {
          const load = event.detail.load;
          load.setColor(this.colorsService.currentColor);
        }
      },
    );
  }

  public changeFloorAction(): void {
    this.projectService.project.subscribe((project) => {
      this.updateVisualisation(project);
    });
  }

  public updateVisualisation(project: Project): void {
    // if (
    //   project.workSpaces[this.currentWorkSpace] !== undefined &&
    //   project.workSpaces[this.currentWorkSpace].cargoSpaces !== undefined &&
    //   project.workSpaces[this.currentWorkSpace].cargoSpaces[0] !== undefined
    // ) {
    //   project.workSpaces[this.currentWorkSpace].cargoSpaces[0].axis =
    //     project.workSpaces[this.currentWorkSpace].axis;
    // }
    const isAxis =
      project.workSpaces[this.currentWorkSpace] &&
      project.workSpaces[this.currentWorkSpace].axis &&
      project.workSpaces[this.currentWorkSpace].axis.length > 0;
    let modifiedProject = project;
    if (isAxis) {
      modifiedProject = this.modifyProjectAxles(project);
    }
    this.projectService.$workingTable.next(this.visualisation);
    if (this.currentStopIndex >= 0) {
      this.visualisation.setSession(
        this.createProjectSessionMapper(
          modifiedProject.workSpaces.length > 0
            ? modifiedProject.workSpaces[this.currentWorkSpace].stops[this.currentStopIndex]
                .cargoSpaces
            : [],
          modifiedProject.workSpaces.length > 0
            ? modifiedProject.workSpaces[this.currentWorkSpace].stops[this.currentStopIndex]
                .outsideSpace
            : undefined,
          modifiedProject.options.unit,
          modifiedProject.workSpaces[this.currentWorkSpace].stops
            ? modifiedProject.workSpaces[this.currentWorkSpace].stops[this.currentStopIndex]
                .unloadingSpace
            : null,
        ),
        undefined,
        modifiedProject.options,
      );
    } else {
      this.visualisation.setSession(
        this.createProjectSessionMapper(
          modifiedProject.workSpaces.length > 0
            ? modifiedProject.workSpaces[this.currentWorkSpace].cargoSpaces
            : [],
          modifiedProject.workSpaces.length > 0
            ? modifiedProject.workSpaces[this.currentWorkSpace].outsideSpace
            : undefined,
          modifiedProject.options.unit,
          modifiedProject.workSpaces[this.currentWorkSpace]?.stops
            ? modifiedProject.workSpaces[this.currentWorkSpace].stops[this.currentStopIndex]
                ?.unloadingSpace
            : null,
        ),
        undefined,
        modifiedProject.options,
      );
    }
  }
}

export class ProjectToSessionMapper implements Session {
  public groups = [];
  public areas = [];
  public truck: LoadingSpaceType;
  public unit: string;
  public outside: OutsideSpace;
  public unloadingSpace: OutsideSpace;

  constructor(
    cargoSpaces: CargoSpace[],
    outsideSpace: OutsideSpace,
    unit: string,
    unloadingSpace?: OutsideSpace,
    allProjectCargoSpaces?: CargoSpace[],
    showPalletContents?: boolean,
  ) {
    cargoSpaces.forEach((cargoSpace: CargoSpace, index) => {
      const groups = expandLinkedPalletContentsForVehicle(
        cargoSpace,
        allProjectCargoSpaces ?? cargoSpaces,
        showPalletContents ?? false,
      );
      groups.forEach((g: Group) => (g.packed = index));
      this.groups = this.groups.concat(groups);
      this.areas = this.areas.concat(cargoSpace.freeSpaces);
    });
    if (unloadingSpace !== undefined && unloadingSpace !== null) {
      unloadingSpace.groups.forEach((g: Group) => (g.packed = undefined));
      this.unloadingSpace = unloadingSpace;
      this.groups = this.groups.concat(unloadingSpace.groups);
      this.areas = this.areas.concat(unloadingSpace.freeSpaces);
    } else {
      const unloadingCopy: Partial<OutsideSpace> = {};
      unloadingCopy.dimensions = null;
      unloadingCopy.position = null;
      this.outside = unloadingCopy as OutsideSpace;
    }
    if (outsideSpace) {
      (outsideSpace.groups ?? []).forEach((g: Group) => (g.packed = undefined));
      this.outside = outsideSpace;
      this.groups = this.groups.concat(outsideSpace.groups ?? []);
      this.areas = this.areas.concat(outsideSpace.freeSpaces ?? []);
    } else {
      const outsideCopy: Partial<OutsideSpace> = {};
      outsideCopy.dimensions = null;
      outsideCopy.position = null;
      this.outside = outsideCopy as OutsideSpace;
    }
    this.truck = this.generateTruckFromCargoSpaces(cargoSpaces);
    this.unit = unit;
  }

  private generateTruckFromCargoSpaces(cargoSpaces: CargoSpace[]): LoadingSpaceType {
    const truck: Partial<LoadingSpaceType> = {};
    if (cargoSpaces.length === 0) {
      truck.length = 0;
      truck.width = 0;
      truck.height = 0;
      truck.length1 = 0;
      truck.width1 = 0;
      truck.height1 = 0;
      truck.type = 3;
      truck.set = false;
      truck.axis = [];
    } else {
      truck.name = cargoSpaces[0].name;
      truck.limit = cargoSpaces[0].limit;
      truck.length = cargoSpaces[0].dimensions.length;
      truck.width = cargoSpaces[0].dimensions.width;
      truck.height = cargoSpaces[0].dimensions.height;
      truck.trailerLoadingSide = cargoSpaces[0].trailerLoadingSide;
      truck.semiTrailerLoadingSide = cargoSpaces[1]?.trailerLoadingSide;
      if (cargoSpaces[0].extraFeatures && cargoSpaces[0].extraFeatures.airType) {
        truck.airType = cargoSpaces[0].extraFeatures.airType;
        truck.airLengthA = cargoSpaces[0].extraFeatures.airLengthA;
        truck.airWidth = cargoSpaces[0].extraFeatures.airWidth;
        truck.airBaseB = cargoSpaces[0].extraFeatures.airBaseB;
        truck.airHeightC = cargoSpaces[0].extraFeatures.airHeightC;
        truck.airHeightD = cargoSpaces[0].extraFeatures.airHeightD;
      }
      truck.length1 = cargoSpaces[1] ? cargoSpaces[1].dimensions.length : 0;
      truck.width1 = cargoSpaces[1] ? cargoSpaces[1].dimensions.width : 0;
      truck.height1 = cargoSpaces[1] ? cargoSpaces[1].dimensions.height : 0;
      truck.limit1 = cargoSpaces[1] ? cargoSpaces[1].limit : 0;
      truck.set = cargoSpaces[1] ? true : false;
      truck.type = cargoSpaces[0].type;
      truck.varioSemiTrailerFloorHeight = cargoSpaces[1]?.extraFeatures?.varioTrailerFloorHeight;
      truck.axis = cargoSpaces[0].axis;
      if (cargoSpaces[1] && cargoSpaces[1].extraFeatures) {
        truck.coolerSemiTrailerWidth = cargoSpaces[1].extraFeatures.coolerTrailerWidth;
        truck.coolerSemiTrailerHeight = cargoSpaces[1].extraFeatures.coolerTrailerHeight;
        truck.coolerSemiTrailerLength = cargoSpaces[1].extraFeatures.coolerTrailerLength;
        truck.coolerSemiTrailerFloorHeight = cargoSpaces[1].extraFeatures.coolerTrailerFloorHeight;
      }

      if (cargoSpaces[0].extraFeatures) {
        truck.varioTrailerFloorHeight = cargoSpaces[0].extraFeatures.varioTrailerFloorHeight;
        truck.varioSemiTrailerFloorHeight = cargoSpaces[1]?.extraFeatures.varioTrailerFloorHeight;
        truck.varioTrailerFloorThickness = cargoSpaces[0].extraFeatures.varioTrailerFloorThickness;
        truck.varioSemiTrailerFloorThickness =
          cargoSpaces[1]?.extraFeatures.varioTrailerFloorThickness;
        truck.varioTrailerFloorCapacity = cargoSpaces[0].extraFeatures.varioTrailerFloorCapacity;
        truck.varioSemiTrailerFloorCapacity =
          cargoSpaces[1]?.extraFeatures.varioTrailerFloorCapacity;
        truck.wheelTrailerWidth = cargoSpaces[0].extraFeatures.wheelTrailerWidth;
        truck.wheelTrailerHeight = cargoSpaces[0].extraFeatures.wheelTrailerHeight;
        truck.wheelTrailerLength = cargoSpaces[0].extraFeatures.wheelTrailerLength;
        truck.wheelTrailerDistance = cargoSpaces[0].extraFeatures.wheelTrailerDistance;
        truck.wheelSemiTrailerWidth = cargoSpaces[1]?.extraFeatures?.wheelTrailerWidth;
        truck.wheelSemiTrailerHeight = cargoSpaces[1]?.extraFeatures?.wheelTrailerHeight;
        truck.wheelSemiTrailerLength = cargoSpaces[1]?.extraFeatures?.wheelTrailerLength;
        truck.wheelSemiTrailerDistance = cargoSpaces[1]?.extraFeatures?.wheelTrailerDistance;
        truck.coolerTrailerWidth = cargoSpaces[0].extraFeatures.coolerTrailerWidth;
        truck.coolerTrailerHeight = cargoSpaces[0].extraFeatures.coolerTrailerHeight;
        truck.coolerTrailerLength = cargoSpaces[0].extraFeatures.coolerTrailerLength;
        truck.coolerTrailerFloorHeight = cargoSpaces[0].extraFeatures.coolerTrailerFloorHeight;
        truck.trailerDoorHeight = cargoSpaces[0].extraFeatures.trailerDoorHeight;
        truck.trailerDoorWidth = cargoSpaces[0].extraFeatures.trailerDoorWidth;
        truck.semiTrailerDoorHeight = cargoSpaces[1]?.extraFeatures?.trailerDoorHeight;
        truck.semiTrailerDoorWidth = cargoSpaces[1]?.extraFeatures?.trailerDoorWidth;
        truck.palletMaxHeight = cargoSpaces[0].extraFeatures.palletMaxHeight;
        truck.palletHeight = cargoSpaces[0].extraFeatures.palletHeight;
        truck.gravityShowCenter = cargoSpaces[0].extraFeatures.gravityShowCenter;
        truck.gravitySemiTrailerLengthTolerance =
          cargoSpaces[0].extraFeatures.gravitySemiTrailerLengthTolerance;
        truck.gravitySemiTrailerWidthTolerance =
          cargoSpaces[0].extraFeatures.gravitySemiTrailerWidthTolerance;
        truck.gravityTrailerLengthTolerance =
          cargoSpaces[0].extraFeatures.gravityTrailerLengthTolerance;
        truck.gravityTrailerWidthTolerance =
          cargoSpaces[0].extraFeatures.gravityTrailerWidthTolerance;
        truck.trailerContainerWeight = cargoSpaces[0].extraFeatures.trailerContainerWeight;
        truck.semiTrailerContainerWeight = cargoSpaces[0].extraFeatures.semiTrailerContainerWeight;
        truck.trailerCOGX = cargoSpaces[0].extraFeatures.trailerCOGX
          ? cargoSpaces[0].extraFeatures.trailerCOGX
          : null;
        truck.trailerCOGY = cargoSpaces[0].extraFeatures.trailerCOGY
          ? cargoSpaces[0].extraFeatures.trailerCOGY
          : null;
        truck.trailerCOGZ = cargoSpaces[0].extraFeatures.trailerCOGZ
          ? cargoSpaces[0].extraFeatures.trailerCOGZ
          : null;
        truck.semiTrailerCOGX =
          cargoSpaces[1] && cargoSpaces[1].extraFeatures.trailerCOGX
            ? cargoSpaces[1].extraFeatures.trailerCOGX
            : null;
        truck.semiTrailerCOGY =
          cargoSpaces[1] && cargoSpaces[1].extraFeatures.trailerCOGY
            ? cargoSpaces[1].extraFeatures.trailerCOGY
            : null;
        truck.semiTrailerCOGZ =
          cargoSpaces[1] && cargoSpaces[1].extraFeatures.trailerCOGZ
            ? cargoSpaces[1].extraFeatures.trailerCOGZ
            : null;
      }
    }
    return truck as LoadingSpaceType;
  }
}

interface CustomEvent extends Event {
  detail: {
    position: any;
    newPosition: any;
    load: Load;
    loadOriginal?: any;
    isBlocked?: boolean;
    intersect: Intersection;
    mousePosition: {
      x: number;
      y: number;
    };
  };
}
